import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Typography,
  Card,
  CardContent,
  CardMedia,
  CardActions,
  Button,
  Chip,
  Stack,
  TextField,
  MenuItem,
  Pagination,
  CircularProgress,
  Alert,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Snackbar
} from '@mui/material';
import Grid from '@mui/material/Grid';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Visibility as ViewIcon,
  FilterList as FilterIcon,
  Search as SearchIcon,
  Pets as PetsIcon,
  CheckBox as CheckboxIcon,
  SelectAll as SelectAllIcon,
  Clear as ClearIcon,
  Publish as PublishIcon,
  Unpublished as UnpublishedIcon,
  Assignment as AssignmentIcon
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@contexts/AuthContext';
import { shelterService } from '@services/sheltersService';
import AnimalViewModal from '@components/shelter-admin/AnimalViewModal';
import AnimalEditModal from '@components/shelter-admin/AnimalEditModal';

interface ShelterAnimal {
  id: string;
  name: string;
  species: 'dog' | 'cat' | 'bird' | 'rabbit' | 'horse' | 'other'; // Standardizat
  breed?: string;
  age_months?: number;
  gender: 'male' | 'female'; // Standardizat (renumit din sex)
  size?: 'mica' | 'medie' | 'mare';
  color?: string;
  status: 'available' | 'reserved' | 'adopted'; // Standardizat
  adoptable: boolean;
  description?: string;
  // Pentru compatibilitate
  sex?: 'M' | 'F'; // Deprecated
  photos?: Array<{
    id: string;
    animal_id: string;
    file_path: string;
    thumbnail_path?: string;
    file_name: string;
    mime_type: string;
    size_bytes: number;
    is_cover: boolean;
    sort_order: number;
    uploaded_at: string;
    caption?: string;
  }>;
  // Coloane pentru poze
  photo_url?: string;
  thumbnail_url?: string;
  photo_filename?: string;
  photo_mime_type?: string;
  photo_size?: number;
  photo_uploaded_at?: string;
  created_at: string;
}

const ShelterAnimalsPage: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [animals, setAnimals] = useState<ShelterAnimal[]>([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [filters, setFilters] = useState({
    species: '',
    status: '',
    ageRange: '',
    gender: '', // Standardizat (renumit din sex)
    size: '',
    compatibility: '',
    microchipped: '',
    intakeDate: '',
    q: ''
  });
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [animalToDelete, setAnimalToDelete] = useState<string | null>(null);
  const [selectedAnimals, setSelectedAnimals] = useState<string[]>([]);
  const [bulkActionDialogOpen, setBulkActionDialogOpen] = useState(false);
  const [bulkAction, setBulkAction] = useState('');
  
  // Modal states
  const [viewModalOpen, setViewModalOpen] = useState(false);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [selectedAnimalId, setSelectedAnimalId] = useState<string | null>(null);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarSeverity, setSnackbarSeverity] = useState<'success' | 'error' | 'info' | 'warning'>('success');

  useEffect(() => {
    loadAnimals();
  }, [page, filters]);

  const loadAnimals = async () => {
    try {
      setLoading(true);
      
      if (!user?.shelterId) {
        throw new Error('Shelter ID not found');
      }

      const response = await shelterService.getShelterAnimals(user.shelterId, {
        page,
        limit: 12,
        species: filters.species || undefined,
        status: filters.status || undefined,
        ageRange: filters.ageRange || undefined,
        sex: filters.gender || undefined, // mapăm UI gender -> API sex
        size: filters.size || undefined,
        compatibility: filters.compatibility || undefined,
        microchipped: filters.microchipped || undefined,
        intakeDate: filters.intakeDate || undefined,
        search: filters.q || undefined
      });

      // API returnează { success: true, animals: [...], total: ... }
      setAnimals(response.animals || []);
      setTotal(response.total || 0);
    } catch (error) {
      console.error('Error loading animals:', error);
      setAnimals([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteAnimal = async (animalId: string) => {
    try {
      await shelterService.deleteShelterAnimal(animalId);
      setAnimals(animals.filter(animal => animal.id !== animalId));
      setDeleteDialogOpen(false);
      setAnimalToDelete(null);
      setSnackbarMessage('Animalul a fost șters cu succes!');
      setSnackbarSeverity('success');
      setSnackbarOpen(true);
    } catch (error) {
      console.error('Error deleting animal:', error);
      setSnackbarMessage('Eroare la ștergerea animalului');
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
    }
  };

  const handleViewAnimal = (animalId: string) => {
    setSelectedAnimalId(animalId);
    setViewModalOpen(true);
  };

  const handleEditAnimal = (animalId: string) => {
    setSelectedAnimalId(animalId);
    setEditModalOpen(true);
  };

  const handleAnimalSaved = (updatedAnimal: any) => {
    setAnimals(animals.map(animal => 
      animal.id === updatedAnimal.id ? updatedAnimal : animal
    ));
    setSnackbarMessage('Animalul a fost actualizat cu succes!');
    setSnackbarSeverity('success');
    setSnackbarOpen(true);
  };

  const handleSelectAnimal = (animalId: string) => {
    setSelectedAnimals(prev => 
      prev.includes(animalId) 
        ? prev.filter(id => id !== animalId)
        : [...prev, animalId]
    );
  };

  const handleSelectAll = () => {
    setSelectedAnimals(animals.map(animal => animal.id));
  };

  const handleClearSelection = () => {
    setSelectedAnimals([]);
  };

  const handleBulkAction = async () => {
    try {
      // TODO: Implement bulk actions
      console.log('Bulk action:', bulkAction, 'on animals:', selectedAnimals);
      setBulkActionDialogOpen(false);
      setSelectedAnimals([]);
    } catch (error) {
      console.error('Error performing bulk action:', error);
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'available': return 'success';
      case 'reserved': return 'warning';
      case 'adopted': return 'info';
      // Pentru compatibilitate cu valorile vechi
      case 'Disponibil': return 'success';
      case 'Rezervat': return 'warning';
      case 'Adoptat': return 'info';
      default: return 'default';
    }
  };

  const getSpeciesIcon = (species: string) => {
    return <PetsIcon />;
  };

  return (
    <Container maxWidth="xl" sx={{ py: 4 }}>
      {/* Header */}
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={4}>
        <Box>
          <Typography variant="h4" fontWeight="bold" gutterBottom>
            Gestionare Animale
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Gestionează animalele din adapost
          </Typography>
        </Box>
        {user?.role === 'shelter_admin' && (
          <Button
            variant="contained"
            color="primary"
            startIcon={<AddIcon />}
            onClick={() => navigate('/shelter/animals/new')}
            size="large"
          >
            Adaugă Animal
          </Button>
        )}
      </Box>

      {/* Filtre Avansate */}
      <Card sx={{ mb: 4 }}>
        <CardContent>
          <Typography variant="h6" fontWeight="bold" mb={2}>
            Filtre Avansate
          </Typography>
          
          {/* Primul rând - Căutare și filtre de bază */}
          <Grid container spacing={2} mb={2}>
            <Grid size={{ xs: 12, md: 3 }}>
              <TextField
                fullWidth
                size="small"
                label="Caută animal"
                placeholder="Nume, rasă, microcip..."
                value={filters.q}
                onChange={(e) => setFilters({ ...filters, q: e.target.value })}
                InputProps={{
                  startAdornment: <SearchIcon sx={{ mr: 1, color: 'text.secondary' }} />
                }}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 3 }}>
              <TextField
                fullWidth
                size="small"
                select
                label="Specie"
                value={filters.species}
                onChange={(e) => setFilters({ ...filters, species: e.target.value })}
              >
                <MenuItem value="">Toate speciile</MenuItem>
                <MenuItem value="dog">Câine</MenuItem>
                <MenuItem value="cat">Pisică</MenuItem>
                <MenuItem value="bird">Pasăre</MenuItem>
                <MenuItem value="rabbit">Iepure</MenuItem>
                <MenuItem value="horse">Cal</MenuItem>
                <MenuItem value="other">Altul</MenuItem>
              </TextField>
            </Grid>
            <Grid size={{ xs: 12, md: 3 }}>
              <TextField
                fullWidth
                size="small"
                select
                label="Status"
                value={filters.status}
                onChange={(e) => setFilters({ ...filters, status: e.target.value })}
              >
                <MenuItem value="">Toate statusurile</MenuItem>
                <MenuItem value="available">Disponibil</MenuItem>
                <MenuItem value="reserved">Rezervat</MenuItem>
                <MenuItem value="adopted">Adoptat</MenuItem>
              </TextField>
            </Grid>
            <Grid size={{ xs: 12, md: 3 }}>
              <TextField
                fullWidth
                size="small"
                select
                label="Gen"
                value={filters.gender}
                onChange={(e) => setFilters({ ...filters, gender: e.target.value })}
              >
                <MenuItem value="">Toate</MenuItem>
                <MenuItem value="male">Mascul</MenuItem>
                <MenuItem value="female">Femelă</MenuItem>
              </TextField>
            </Grid>
          </Grid>

          {/* Al doilea rând - Filtre detaliate */}
          <Grid container spacing={2} mb={2}>
            <Grid size={{ xs: 12, md: 3 }}>
              <TextField
                fullWidth
                size="small"
                select
                label="Vârstă"
                value={filters.ageRange}
                onChange={(e) => setFilters({ ...filters, ageRange: e.target.value })}
              >
                <MenuItem value="">Toate vârstele</MenuItem>
                <MenuItem value="0-6">0-6 luni</MenuItem>
                <MenuItem value="6-12">6-12 luni</MenuItem>
                <MenuItem value="1-3">1-3 ani</MenuItem>
                <MenuItem value="3-7">3-7 ani</MenuItem>
                <MenuItem value="7+">7+ ani</MenuItem>
              </TextField>
            </Grid>
            <Grid size={{ xs: 12, md: 3 }}>
              <TextField
                fullWidth
                size="small"
                select
                label="Talie"
                value={filters.size}
                onChange={(e) => setFilters({ ...filters, size: e.target.value })}
              >
                <MenuItem value="">Toate tăliile</MenuItem>
                <MenuItem value="mica">Mică</MenuItem>
                <MenuItem value="medie">Medie</MenuItem>
                <MenuItem value="mare">Mare</MenuItem>
              </TextField>
            </Grid>
            <Grid size={{ xs: 12, md: 3 }}>
              <TextField
                fullWidth
                size="small"
                select
                label="Compatibilități"
                value={filters.compatibility}
                onChange={(e) => setFilters({ ...filters, compatibility: e.target.value })}
              >
                <MenuItem value="">Toate</MenuItem>
                <MenuItem value="kids">Bun cu copiii</MenuItem>
                <MenuItem value="dogs">Bun cu câinii</MenuItem>
                <MenuItem value="cats">Bun cu pisicile</MenuItem>
                <MenuItem value="only_pet">Singurul animal</MenuItem>
              </TextField>
            </Grid>
            <Grid size={{ xs: 12, md: 3 }}>
              <TextField
                fullWidth
                size="small"
                select
                label="Microcip"
                value={filters.microchipped}
                onChange={(e) => setFilters({ ...filters, microchipped: e.target.value })}
              >
                <MenuItem value="">Toate</MenuItem>
                <MenuItem value="yes">Da</MenuItem>
                <MenuItem value="no">Nu</MenuItem>
              </TextField>
            </Grid>
          </Grid>

          {/* Al treilea rând - Filtre suplimentare */}
          <Grid container spacing={2}>
            <Grid size={{ xs: 12, md: 6 }}>
              <TextField
                fullWidth
                size="small"
                type="date"
                label="Data sosire de la"
                value={filters.intakeDate}
                onChange={(e) => setFilters({ ...filters, intakeDate: e.target.value })}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <Stack direction="row" spacing={1}>
                <Button
                  variant="outlined"
                  size="small"
                  onClick={() => setFilters({
                    species: '',
                    status: '',
                    ageRange: '',
                    gender: '',
                    size: '',
                    compatibility: '',
                    microchipped: '',
                    intakeDate: '',
                    q: ''
                  })}
                >
                  Resetează Filtrele
                </Button>
                <Button
                  variant="contained"
                  size="small"
                  startIcon={<FilterIcon />}
                >
                  Aplică Filtrele
                </Button>
              </Stack>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Bulk Actions */}
      {selectedAnimals.length > 0 && (
        <Card sx={{ mb: 4, bgcolor: 'primary.light', color: 'primary.contrastText' }}>
          <CardContent>
            <Stack direction="row" alignItems="center" justifyContent="space-between">
              <Typography variant="h6" fontWeight="bold">
                {selectedAnimals.length} animale selectate
              </Typography>
              <Stack direction="row" spacing={1}>
                {user?.role === 'shelter_admin' && (
                  <Button
                    variant="outlined"
                    size="small"
                    startIcon={<PublishIcon />}
                    onClick={() => {
                      setBulkAction('publish');
                      setBulkActionDialogOpen(true);
                    }}
                  >
                    Publică
                  </Button>
                )}
                {user?.role === 'shelter_admin' && (
                  <Button
                    variant="outlined"
                    size="small"
                    startIcon={<UnpublishedIcon />}
                    onClick={() => {
                      setBulkAction('unpublish');
                      setBulkActionDialogOpen(true);
                    }}
                  >
                    Ascunde
                  </Button>
                )}
                {user?.role === 'shelter_admin' && (
                  <Button
                    variant="outlined"
                    size="small"
                    startIcon={<AssignmentIcon />}
                    onClick={() => {
                      setBulkAction('assign_volunteer');
                      setBulkActionDialogOpen(true);
                    }}
                  >
                    Atribuie Voluntar
                  </Button>
                )}
                {user?.role === 'shelter_admin' && (
                  <Button
                    variant="outlined"
                    size="small"
                    startIcon={<EditIcon />}
                    onClick={() => {
                      setBulkAction('change_status');
                      setBulkActionDialogOpen(true);
                    }}
                  >
                    Schimbă Starea
                  </Button>
                )}
                <Button
                  variant="outlined"
                  size="small"
                  startIcon={<ClearIcon />}
                  onClick={handleClearSelection}
                >
                  Anulează Selecția
                </Button>
              </Stack>
            </Stack>
          </CardContent>
        </Card>
      )}

      {/* Lista Animale */}
      {loading ? (
        <Box display="flex" justifyContent="center" py={8}>
          <CircularProgress />
        </Box>
      ) : animals.length === 0 ? (
        <Alert severity="info">
          Nu ai animale în adapost. Adaugă primul animal pentru a începe!
        </Alert>
      ) : (
        <>
          <Grid container spacing={3}>
            {animals.map((animal) => (
              <Grid size={{ xs: 12, sm: 6, md: 4 }} key={animal.id}>
                <Card 
                  sx={{ 
                    height: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    transition: 'transform 0.2s, box-shadow 0.2s',
                    border: selectedAnimals.includes(animal.id) ? '2px solid' : '1px solid',
                    borderColor: selectedAnimals.includes(animal.id) ? 'primary.main' : 'divider',
                    '&:hover': {
                      transform: 'translateY(-4px)',
                      boxShadow: 4
                    }
                  }}
                >
                  <CardMedia
                    component="img"
                    height="200"
                    image={(() => {
                      // Caută prima poză din array-ul de poze
                      const firstPhoto = animal.photos?.[0];
                      if (firstPhoto) {
                        const photoUrl = firstPhoto.file_path || firstPhoto.thumbnail_path;
                        if (photoUrl) {
                          if (photoUrl.startsWith('http')) return photoUrl;
                          // Pentru URL-uri relative, folosește API_URL din serviciu
                          const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3000/api/v1';
                          const baseURL = API_URL.replace('/api/v1', '');
                          return `${baseURL}${photoUrl}`;
                        }
                      }
                      
                      // Fallback la pozele vechi din animal
                      const photoUrl = animal.photo_url || animal.thumbnail_url;
                      if (photoUrl) {
                        if (photoUrl.startsWith('http')) return photoUrl;
                        const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3000/api/v1';
                        const baseURL = API_URL.replace('/api/v1', '');
                        return `${baseURL}${photoUrl}`;
                      }
                      
                      return undefined;
                    })()}
                    alt={animal.name}
                    sx={{
                      bgcolor: 'primary.light',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      objectFit: 'cover'
                    }}
                    onError={(e) => {
                      // Dacă poza nu se încarcă, afișează iconița
                      const target = e.target as HTMLImageElement;
                      target.style.display = 'none';
                      const parent = target.parentElement;
                      if (parent) {
                        parent.innerHTML = `<div style="display: flex; align-items: center; justify-content: center; height: 200px; background-color: #e3f2fd;">${getSpeciesIcon(animal.species).props.children}</div>`;
                      }
                    }}
                  />
                  
                  <CardContent sx={{ flexGrow: 1 }}>
                    <Stack direction="row" justifyContent="space-between" alignItems="center" mb={1}>
                      <Stack direction="row" alignItems="center" spacing={1}>
                        <IconButton
                          size="small"
                          onClick={() => handleSelectAnimal(animal.id)}
                          sx={{ 
                            color: selectedAnimals.includes(animal.id) ? 'primary.main' : 'text.secondary',
                            p: 0.5
                          }}
                        >
                          <CheckboxIcon />
                        </IconButton>
                        <Typography variant="h6" fontWeight="bold">
                          {animal.name}
                        </Typography>
                      </Stack>
                      <Chip 
                        label={animal.status} 
                        color={getStatusColor(animal.status) as any}
                        size="small"
                      />
                    </Stack>
                    
                    <Stack spacing={1} mb={2}>
                      <Typography variant="body2" color="text.secondary">
                        <strong>Specie:</strong> {animal.species}
                      </Typography>
                      {animal.breed && (
                        <Typography variant="body2" color="text.secondary">
                          <strong>Rasă:</strong> {animal.breed}
                        </Typography>
                      )}
                      <Typography variant="body2" color="text.secondary">
                        <strong>Vârstă:</strong> {animal.age_months} luni
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        <strong>Gen:</strong> {animal.gender === 'male' ? 'Mascul' : animal.gender === 'female' ? 'Femelă' : (animal as any).sex === 'M' ? 'Mascul' : 'Femelă'}
                      </Typography>
                    </Stack>
                    
                    {animal.description && (
                      <Typography 
                        variant="body2" 
                        color="text.secondary"
                        sx={{ 
                          display: '-webkit-box',
                          WebkitLineClamp: 2,
                          WebkitBoxOrient: 'vertical',
                          overflow: 'hidden'
                        }}
                      >
                        {animal.description}
                      </Typography>
                    )}
                  </CardContent>
                  
                  <CardActions sx={{ p: 2, pt: 0 }}>
                    <Button 
                      size="small" 
                      startIcon={<ViewIcon />}
                      onClick={() => handleViewAnimal(animal.id)}
                    >
                      Vezi
                    </Button>
                    {user?.role === 'shelter_admin' && (
                      <Button 
                        size="small" 
                        startIcon={<EditIcon />}
                        onClick={() => handleEditAnimal(animal.id)}
                      >
                        Editează
                      </Button>
                    )}
                    {user?.role === 'shelter_admin' && (
                      <IconButton
                        size="small"
                        color="error"
                        onClick={() => {
                          setAnimalToDelete(animal.id);
                          setDeleteDialogOpen(true);
                        }}
                      >
                        <DeleteIcon />
                      </IconButton>
                    )}
                  </CardActions>
                </Card>
              </Grid>
            ))}
          </Grid>

          {/* Paginare */}
          {total > 12 && (
            <Box display="flex" justifyContent="center" mt={4}>
              <Pagination
                count={Math.ceil(total / 12)}
                page={page}
                onChange={(_, value) => setPage(value)}
                color="primary"
                size="large"
              />
            </Box>
          )}
        </>
      )}

      {/* Dialog ștergere */}
      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
        <DialogTitle>Confirmă ștergerea</DialogTitle>
        <DialogContent>
          <Typography>
            Ești sigur că vrei să ștergi acest animal? Această acțiune este ireversibilă.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>Anulează</Button>
          <Button 
            color="error" 
            variant="contained"
            onClick={() => animalToDelete && handleDeleteAnimal(animalToDelete)}
          >
            Șterge
          </Button>
        </DialogActions>
      </Dialog>

      {/* Bulk Action Dialog */}
      <Dialog open={bulkActionDialogOpen} onClose={() => setBulkActionDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Acțiune în Masă</DialogTitle>
        <DialogContent>
          <Typography variant="body1" mb={2}>
            Ești sigur că vrei să aplici acțiunea "{bulkAction}" pentru {selectedAnimals.length} animale selectate?
          </Typography>
          {bulkAction === 'change_status' && (
            <TextField
              fullWidth
              select
              label="Noua stare"
              value=""
              onChange={() => {}}
              sx={{ mt: 2 }}
            >
              <MenuItem value="available">Disponibil</MenuItem>
              <MenuItem value="reserved">Rezervat</MenuItem>
              <MenuItem value="adopted">Adoptat</MenuItem>
              <MenuItem value="Scos din listă">Scos din listă</MenuItem>
            </TextField>
          )}
          {bulkAction === 'assign_volunteer' && (
            <TextField
              fullWidth
              label="Nume voluntar"
              placeholder="Introdu numele voluntarului"
              sx={{ mt: 2 }}
            />
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setBulkActionDialogOpen(false)}>Anulează</Button>
          <Button variant="contained" onClick={handleBulkAction}>
            Confirmă Acțiunea
          </Button>
        </DialogActions>
      </Dialog>

      {/* Animal View Modal */}
      <AnimalViewModal
        open={viewModalOpen}
        onClose={() => {
          setViewModalOpen(false);
          setSelectedAnimalId(null);
        }}
        animalId={selectedAnimalId}
        onEdit={handleEditAnimal}
      />

      {/* Animal Edit Modal */}
      <AnimalEditModal
        open={editModalOpen}
        onClose={() => {
          setEditModalOpen(false);
          setSelectedAnimalId(null);
        }}
        animalId={selectedAnimalId}
        onSave={handleAnimalSaved}
      />

      {/* Snackbar pentru notificări */}
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={6000}
        onClose={() => setSnackbarOpen(false)}
        message={snackbarMessage}
      />
    </Container>
  );
};

export default ShelterAnimalsPage;
